import React, { useRef, useState } from 'react';
import * as Yup from 'yup';
import { connect } from 'react-redux';
import { ErrorMessage, Field, Formik, FormikProps } from 'formik';
import { default as lodashFirst } from 'lodash/first';
import './index.scss';
import { AppState } from '$/store';
import { ClientAdminService } from '$/services';
import { toastSuccess, toastError } from '$/store/toast/actions';
import BixButton from '$/components/global/bixButton';
import Modal from '$/components/global/modal';

interface IBulkImportCompanies {
  active: boolean;
  clientId: string | null;
  onImportSubmit: () => void;
  onCloseModal: () => void;
  dispatchToastSuccess: (message: string) => void;
  dispatchToastError: (message: string) => void;
}

interface CompanyValues {
  client_Id: string | null;
  companiesFile: File | null;
}

const BulkImportCompanies: React.FC<IBulkImportCompanies> = props => {
  const { clientId, active, onImportSubmit, onCloseModal, dispatchToastSuccess, dispatchToastError } = props;

  const [isImporting, setIsImporting] = useState(false);
  const [importError, setImportError] = useState(false);
  const [importErrorMessage, setImportErrorMessage] = useState<string>();

  const formikRef = useRef<FormikProps<CompanyValues>>(null);

  const handleCloseModal = () => {
    setImportError(false);
    setImportErrorMessage(undefined);
    setIsImporting(false);

    if (formikRef && formikRef.current) {
      formikRef.current.resetForm();
    }

    onCloseModal();
  };

  const importCompaniesAsync = async (values: CompanyValues) => {
    if (!values.client_Id || !values.companiesFile) return;

    try {
      setImportError(false);
      setImportErrorMessage(undefined);
      setIsImporting(true);

      await ClientAdminService.importCompanies(values.client_Id, values.companiesFile);

      if (formikRef && formikRef.current) {
        formikRef.current.resetForm();
      }

      dispatchToastSuccess('File submitted');
      // Closes the Modal
      onImportSubmit();
    } catch (err: any) {
      setImportError(true);
      let toastMessage = 'Error importing data';
      let message = 'Error importing data';
      if (err && typeof err === 'object') {
        message = err.response?.data || err.message || message;
      } else if (typeof err === 'string') {
        message = err;
      }

      if (message.indexOf('does not match client name') > -1) {
        toastMessage = message;
      }
      dispatchToastError(toastMessage);
      setImportErrorMessage(message);
    } finally {
      setIsImporting(false);
    }
  };

  const getTemplateFile = async () => {
    await ClientAdminService.getTemplateFile();
  };

  const getExtension = fileName => {
    const extension = fileName.substring(fileName.lastIndexOf('.') + 1);
    return extension;
  };

  const schema = Yup.object({
    client_Id: Yup.string().required('Client is required'),
    companiesFile: Yup.mixed()
      .required('File is required')
      .test({
        message: 'Please provide a supported file type',
        test: (file, context) => {
          const isValid = ['xlsx', 'xls'].includes(getExtension((file as File).name));
          if (!isValid) context?.createError();
          return isValid;
        },
      }),
  });

  const initialValues: CompanyValues = {
    client_Id: clientId,
    companiesFile: null,
  };

  return (
    <Modal title="Bulk Associate Companies" active={active} forForm onClose={handleCloseModal}>
      <Formik
        innerRef={formikRef}
        initialValues={initialValues}
        validationSchema={schema}
        onSubmit={async (values: CompanyValues) => {
          await importCompaniesAsync(values);
        }}
        render={({ handleSubmit, values }) => (
          <form className="import-company-form" onSubmit={handleSubmit}>
            {values.client_Id && (
              <>
                <div className="search_block">
                  <Field
                    name="companiesFile"
                    render={({ form }: { [key: string]: any }) => {
                      const handleUserFileChange = (ev: React.FormEvent<HTMLInputElement>) => {
                        const file = lodashFirst(ev.currentTarget.files);
                        form.setFieldValue('companiesFile', file);
                      };
                      return (
                        <>
                          <div className="search_block_title">{'Import File'}</div>
                          <input
                            name="companiesFile"
                            type="file"
                            disabled={isImporting}
                            onChange={handleUserFileChange}
                          />
                          <span className="template_link" onClick={getTemplateFile}>
                            Download Import Template.xlsx
                          </span>
                        </>
                      );
                    }}
                  />
                </div>
                <ErrorMessage name="companiesFile">
                  {msg => <div className="error error-message">{msg}</div>}
                </ErrorMessage>
              </>
            )}
            <div className="btn_container">
              <BixButton useLoading isLoading={isImporting} disabled={isImporting} type="submit">
                Import Companies
              </BixButton>
            </div>
            {!!importError && (
              <>
                <p className="error">{importErrorMessage}</p>
              </>
            )}
          </form>
        )}
      />
    </Modal>
  );
};

const mapDispatchToProps = (dispatch: any) => ({
  dispatchToastSuccess: (message: string) => dispatch(toastSuccess(message)),
  dispatchToastError: (message: string) => dispatch(toastError(message)),
});

const mapStateToProps = (state: AppState) => {
  return {
    clients: state.clientState.clients,
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(BulkImportCompanies);
